import { useEffect, useMemo } from 'react';
import { findDocumentationOnLevelsAndSites } from 'shared/domain/documentation/findDocumentationOnLevels';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import type { DocumentationBaseIds } from 'shared/types/documentation';
import { useLevels } from 'store/level';
import { buildIssuesOnMapQuery } from '../models/issuesOnMapQuery';
import { useIssueStore } from '../store';
import { useIssuesQuery } from './useIssuesQuery';

/**
 * The issues on the levels whose map is this documentation file, with the
 * grid's filters; read while a component shows them.
 */
export function useIssuesOnDocumentation(file: DocumentationBaseIds): {
  issues: IssueModel[];
  isUsedAsLevel: boolean;
  loadingLevels: boolean;
} {
  const { loading: loadingLevels, items: levels } = useLevels();
  const { levelsValue } = useMemo(
    () => findDocumentationOnLevelsAndSites(levels, [], file.documentationId),
    [levels, file.documentationId]
  );
  const gridQuery = useIssuesQuery();
  const loadIssuesOnMap = useIssueStore.use.loadIssuesOnMap();
  const issues = useIssueStore.use.issuesOnMap();

  useEffect(() => {
    if (gridQuery && levelsValue.length > 0) {
      void loadIssuesOnMap(buildIssuesOnMapQuery(levelsValue, gridQuery));
    }
  }, [gridQuery, levelsValue, loadIssuesOnMap]);

  return { issues, isUsedAsLevel: levelsValue.length > 0, loadingLevels };
}

/** Keeps the map's issues in line with issue changes while the documentation view is open; clears them when it closes. */
export function useFollowIssuesOnMap(): void {
  const followIssuesOnMap = useIssueStore.use.followIssuesOnMap();
  useEffect(() => followIssuesOnMap(), [followIssuesOnMap]);
}
